import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router';

interface UserGuideProps {
  otherClasses?: string;
}

const UserGuide: React.FC<UserGuideProps> = ({ otherClasses }) => {
  const userGuideClasses = clsx(
    otherClasses,
    'px-15 md:px-6 lg:pl-8 pt-14 lg:pr-14 pb-9 w-full',
  );

  const dispatch = useDispatch();

  return (
    <div className={userGuideClasses} data-testid="user-guide">
      <div className="border-b-neutral-tones-text/35 border-b pb-6 lg:border-b-0 lg:pb-0">
        <h3 className="text-brand-byzantine hidden lg:block">User Guide</h3>
        <div className="flex items-center gap-3 lg:hidden">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <h3 className="text-brand-byzantine lg:hidden">User Guide</h3>
        </div>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-lg leading-5 font-medium lg:block lg:text-xl lg:leading-6">
          Learn how to use EHS Navigator or visit our comprehensive{' '}
          <Link
            to="http://docs.ehsnavigator.com/"
            className="text-brand-byzantine font-bold hover:underline"
          >
            Guide
          </Link>{' '}
          for a detailed, step-by-step overview.
        </p>
      </div>
      <div className="mt-6 flex flex-col gap-10 lg:mt-12">
        <div>
          <h4 className="mb-4 tracking-normal">🔐 1. Getting Started</h4>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              After registration, the user becomes the <strong>Owner</strong> of
              the organization.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Your organization starts with a <strong>7-day free trial</strong>,
              full access to all features, and can onboard{' '}
              <strong>up to 10 users.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              During signup, you can either{' '}
              <strong>choose a subscription plan</strong> or{' '}
              <strong>start with the free trial.</strong>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">👥 2. User Management</h4>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Available to <strong>Owner and Admin</strong> roles only.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Start by creating <strong>Departments</strong> via{' '}
              <strong>User Management {`>`} Departments.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Add users (Admins, Managers, Employees) and assign roles.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Roles can be updated anytime from{' '}
              <strong>User Management {`>`} User Roles.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Admins</strong> can add, update, and delete the Managers
              and Employees, as well as manage roles.
            </li>
          </ul>
          <div className="border-l-neutral-tones-text/20 mt-4 border-l pl-6">
            <h5 className="text-button text-neutral-tones-text mb-4 font-bold tracking-normal">
              2.1 Bulk User Creation
            </h5>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Available to <strong>Owner and Admins</strong> via{' '}
              <strong>User Management {`>`} Bulk Upload</strong>.
            </p>
            <ul className="my-4 flex list-disc flex-col gap-2 pl-4">
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                <strong>Step 1 – Upload:</strong> Download the sample XLSX, fill
                in user details, and upload XLSX/CSV file.
              </li>
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                <strong>Step 2 – Validation & Editing:</strong> Review uploaded
                records in an editable table. Correct errors, add/remove rows,
                and pass all validation checks before proceeding.
              </li>
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                <strong>Step 3 – Final Review:</strong> Review validated records
                before submission.
              </li>
            </ul>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Task Review:</strong>
            </p>
            <ul className="mt-4 flex list-disc flex-col gap-2 pl-4">
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                Track bulk upload progress and view task history (last 2
                months).
              </li>
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                Drill down into successful and failed records with error
                reasons.
              </li>
            </ul>
            <p className="text-neutral-tones-text font-inter-variable mt-4 text-base leading-5 font-normal">
              <strong>Notifications:</strong>
            </p>
            <ul className="mt-4 flex list-disc flex-col gap-2 pl-4">
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                Initiating Admin receives completion email.
              </li>
              <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                The <strong>Owner of the organization</strong> will also receive
                an email summarizing how many employees, managers, and admins
                were added during the process.
              </li>
            </ul>
          </div>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">📄 3. Incident Reporting</h4>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Any user can report incidents via{' '}
              <strong>Incidents {`>`} Report Incident.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Incidents can be saved as <strong>drafts</strong> before
              submission.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              On submission, the <strong>Owner, Admins, and Managers</strong>{' '}
              are notified via email.
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">
            🛠️ 4. Incident Review & Corrective Actions
          </h4>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Accessible to <strong>Owner, Admins, and Managers</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              View all organisation incidents via{' '}
              <strong>Manager Review {`>`} Incident Review.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Owner, Admins, and Managers</strong> can:
              <ol className="mt-2 flex list-decimal flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Assign <strong>Corrective Actions</strong> to individuals or
                  teams
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Save corrective actions as drafts
                </li>
              </ol>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              For multi-assignee actions:
              <ol className="mt-2 flex list-decimal flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  A <strong>Lead Assignee</strong> is required and is
                  responsible for:
                  <ul className="mt-2 flex list-[square] flex-col gap-2 pl-4">
                    <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      Sending the action for review
                    </li>
                    <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                      Requesting due date extension
                    </li>
                  </ul>
                </li>
              </ol>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">
            ✅ 5. Managing Corrective Actions
          </h4>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Assigned to Me:</strong> View corrective actions assigned
              to you under{' '}
              <strong>Corrective Actions {`>`} Assigned to Me.</strong> Anyone
              can be assigned a corrective action.
              <ol className="mt-2 flex list-decimal flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  You can mark for review, pull back, or request an extension.
                </li>
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Add notes or assets (images or documents) to support your
                  submission.
                </li>
              </ol>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Assigned by Me:</strong> See actions you’ve assigned via{' '}
              <strong>Manager Review {`>`} Assigned Corrective Action.</strong>{' '}
              Accessible to <strong>Owner, Admins, and Managers</strong>
              <ol className="mt-2 flex list-decimal flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  You can approve/reject the review or handle extension
                  requests.
                </li>
              </ol>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>All Corrective Actions:</strong> View across the org via{' '}
              <strong>Corrective Actions {`>`} All Corrective Actions.</strong>
              <ol className="mt-2 flex list-decimal flex-col gap-2 pl-4">
                <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                  Anyone (Owner, Admins, and Managers) can edit; only the
                  assigner can approve, reject, or manage extension requests.
                </li>
              </ol>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">
            🩺 6. Injuries & Investigation Tool
          </h4>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            Accessible to <strong>Owner, Admins, and Managers</strong> via{' '}
            <strong>Injuries</strong> in the sidebar.
          </p>
          <p className="text-neutral-tones-text font-inter-variable mt-4 text-base leading-5 font-normal">
            <strong>Automatic Tracking:</strong>
          </p>
          <ul className="mt-4 flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Any reported incident under <strong>Injury/Illness</strong>{' '}
              automatically appears in this section.
            </li>
          </ul>
          <p className="text-neutral-tones-text font-inter-variable mt-4 text-base leading-5 font-normal">
            <strong>Investigation Workflow:</strong>
          </p>
          <ol className="my-4 flex list-decimal flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Default Information</strong> – Capture key details (injury
              type, type of loss, affected body parts, classification,
              date/time, location). Optional report upload.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>5 Whys Questions</strong> – Guided root cause questions,
              including OSHA-related fields.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Organizational Questions</strong> – Custom questions
              defined in <strong>My Account {`>`} Custom Questions</strong>.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Custom Questions & Root Cause</strong> – Add
              incident-specific questions and provide a root cause analysis
              summary.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Review</strong> – Final categorized review of all inputs
              before submission.
            </li>
          </ol>
          <p className="text-neutral-tones-text font-inter-variable mt-4 text-base leading-5 font-normal">
            <strong>Additional Features:</strong>
          </p>
          <ul className="mt-4 flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Investigations can be edited after submission.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Management receives email notifications when investigations are
              submitted.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              History tracking is maintained.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Data can be searched, filtered, and exported (CSV/XLSX).
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Compatible with OSHA reporting (records populate OSHA module).
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">🗂️ 7. Document Library</h4>
          <p className="text-neutral-tones-text font-inter-variable mb-2 text-base leading-5 font-normal">
            The <strong>Document Library</strong> allows users to securely
            upload, access, search, and manage organizational and personal
            documents in one centralized location.
          </p>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong> Upload Documents:</strong> Upload Word, PDF, or other
              supported file formats.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Access Control:</strong> View all documents available in
              your organization, including personal uploads.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Search Functionality:</strong> Search by{' '}
              <strong>title, tags, or author.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Content-Based Search:</strong> The system now parses
              document content to provide more relevant search results.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Download/Delete:</strong> Users can{' '}
              <strong>download</strong> or <strong>delete</strong> their
              documents as needed.
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">🏥 8. OSHA Reporting</h4>
          <p className="text-neutral-tones-text font-inter-variable mb-2 text-base leading-5 font-normal">
            The <strong>OSHA Reporting</strong> module enables organization
            owners and admins to submit OSHA Forms 300 and 300A directly via
            API, ensuring compliance with OSHA injury and illness recordkeeping
            requirements.
          </p>
          <p className="text-neutral-tones-text font-inter-variable mb-2 text-base leading-5 font-normal">
            <strong>⚠️ Note:</strong> OSHA electronic reporting is available
            only between <strong>January 2 and March 2</strong> each year.
          </p>
          <p className="text-neutral-tones-text font-inter-variable my-4 text-xl leading-5 font-normal">
            <strong>Key Features</strong>
          </p>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Reporting Window:</strong> Submissions are accepted only
              from <strong>Jan 2 – Mar 2.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Report Incidents:</strong> Submit Injury/Illness incidents
              via the OSHA Reporting workflow.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Automated Form Population:</strong> Form 300 and 300A
              fields are dynamically populated from completed investigations.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Establishment Creation:</strong> Required establishment
              details are automatically created before submission.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Manual Inputs:</strong> Add annual average employee count
              and total hours worked for the reporting year.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>View and Manage Records:</strong> Review record details
              and linked investigations directly from the OSHA 300 screen.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Exclusion Control:</strong> Exclude records from
              submission by flagging them.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Submission Logs:</strong> View all submissions in the OSHA
              Logs screen, including success/failure status, with options to{' '}
              <strong>resubmit</strong> failed submissions.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              <strong>Search, Filter, and Export:</strong> Quickly locate and
              manage logs using built-in tools.
            </li>
          </ul>
          <p className="text-neutral-tones-text font-inter-variable my-4 text-xl leading-5 font-normal">
            <strong>How to Use</strong>
          </p>
          <ol className="flex list-decimal flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Navigate to{' '}
              <strong>OSHA Reporting → Form 300 / Form 300A.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Review auto-populated data from completed investigations.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Complete any required manual fields (e.g., employee averages,
              total hours).
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Submit the form to OSHA via API (available only Jan 2–Mar 2).
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              View submission status under <strong>OSHA Logs.</strong>
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              If a submission fails, use the <strong>Resubmit</strong> option.
            </li>
          </ol>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">🕓 9. History Tracking</h4>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              Every incident, corrective action, and investigation includes a
              history of actions performed on the details page.
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 tracking-normal">📧 10. Email Notifications</h4>
          <p className="text-neutral-tones-text font-inter-variable mb-2 text-base leading-5 font-normal">
            Emails are sent when:
          </p>
          <ul className="flex list-disc flex-col gap-2 pl-4">
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              An incident is reported.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              An incident is closed, reopened, or withdrawn.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              A corrective action is assigned or edited.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              A corrective action sent for review or extension is
              requested/approved/rejected.
            </li>
            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              A corrective action is marked for review, pulled back, or
              rejected.
            </li>

            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              When an Admin starts a bulk user creation process, they receive an
              email once it is completed. This email includes details about the
              onboarded users.
            </li>

            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              The Owner of the organization also receives an email summarizing
              the number of employees, managers, and admins added during the
              bulk upload process.
            </li>

            <li className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              When an investigation is created or edited, the respective
              management receives an email notification upon submission.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;
